import { api } from './api'
import { getAll } from './pagination'

export interface ItemCategory {
  id: number
  name: string
  description: string
  parent: number | null
  children?: ItemCategory[]
  created_at: string
  updated_at: string
}

export interface InventoryItem {
  id: number
  name: string
  description: string
  category: number
  sku: string
  item_type: string
  category_name: string
  unit_cost: string
  current_stock: number
  minimum_stock: number
  maximum_stock: number
  reorder_level: number
  unit_of_measure: string
  storage_location: string | null
  barcode?: string | null
  notes?: string | null
  is_low_stock: boolean
  is_out_of_stock: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface AssetStatusChange {
  id: number
  asset: number
  from_status: string
  to_status: string
  reason: string
  changed_by: number | null
  changed_by_name?: string
  created_at: string
}
export interface Supplier {
  id: number
  name: string
  contact_person: string | null
  email: string | null
  phone: string | null
  address: string | null
  website?: string | null
  payment_terms?: string | null
  credit_limit?: string | null
  rating?: number
  notes?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface PurchaseOrder {
  id: number
  supplier: number
  order_date: string
  expected_delivery_date: string
  order_number: string
  supplier_name: string
  status: 'draft' | 'pending' | 'approved' | 'ordered' | 'received' | 'cancelled'
  approved_date?: string | null
  total_amount: string
  subtotal: string
  tax_amount: string
  actual_delivery_date: string | null
  requested_by: number
  requested_by_name: string
  approved_by: number | null
  approved_by_name: string
  notes: string
  items: PurchaseOrderItem[]
  created_at: string
  updated_at: string
}

export interface PurchaseOrderItem {
  id: number
  item: number
  item_name: string
  item_sku: string
  quantity_ordered: number
  quantity_received: number
  outstanding_quantity: number
  unit_cost: string
  total_cost: string
  is_received: boolean
}

export interface PurchaseOrderInput {
  order_number?: string
  supplier: number
  order_date: string
  expected_delivery_date?: string
  tax_amount?: string
  notes?: string
  items: Array<{ item: number; quantity_ordered: number; unit_cost: string }>
}

export interface StockMovement {
  id: number
  item: number
  item_name: string
  item_sku: string
  movement_type: string
  quantity: number
  quantity_before: number
  quantity_after: number
  reference_number: string | null
  reason: string | null
  reference_type?: string | null
  performed_by_name: string
  created_at: string
}

export interface InventoryAlert {
  id: number
  alert_type: string
  priority: string
  title: string
  message: string
  item: number | null
  item_name: string | null
  asset?: number | null
  asset_name?: string | null
  is_read: boolean
  acknowledged_date?: string | null
  acknowledged_by_name: string | null
  action_taken: string
  created_at: string
}

export interface Asset {
  id: number
  name: string
  description: string | null
  asset_type: 'furniture' | 'equipment' | 'technology' | 'vehicle' | 'building' | 'land' | 'other'
  category: number
  category_name: string
  asset_tag: string
  serial_number: string | null
  model_number?: string | null
  manufacturer: string | null
  notes?: string | null
  assigned_to_name?: string
  purchase_price: string
  current_value: string | null
  net_book_value: string
  depreciation_rate: string
  purchase_date: string
  warranty_expiry: string | null
  last_maintenance_date: string | null
  next_maintenance_date: string | null
  location: string | null
  status: 'active' | 'inactive' | 'maintenance' | 'disposed' | 'lost' | 'stolen'
  condition: 'excellent' | 'good' | 'fair' | 'poor' | 'damaged'
  supplier: number | null
  supplier_name?: string
  is_capitalized: boolean
  capitalized_at: string | null
  capitalized_by_name?: string
  last_depreciated_on: string | null
  disposal_date: string | null
  disposal_proceeds: string | null
  created_at: string
}

export interface MaintenanceRecord {
  id: number
  asset: number
  asset_name: string
  asset_tag: string
  maintenance_type: 'preventive' | 'corrective' | 'emergency' | 'upgrade'
  title: string
  description: string
  performed_by: string | null
  scheduled_date: string | null
  performed_date: string | null
  next_maintenance_date: string | null
  labor_cost: string
  parts_cost: string
  total_cost: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  work_performed: string | null
  parts_replaced?: string | null
  recommendations?: string | null
  recorded_by_name: string
  created_at: string
}

export interface InventoryStats {
  total_items: number
  low_stock_items: number
  out_of_stock_items: number
  total_assets: number
  active_assets: number
  total_suppliers: number
  pending_orders: number
}

export const inventoryService = {
  // Item Categories
  getItemCategories: async (): Promise<ItemCategory[]> => {
    return getAll('/inventory/categories/')
  },

  getItemCategory: async (id: number): Promise<ItemCategory> => {
    const response = await api.get(`/inventory/categories/${id}/`)
    return response.data
  },

  createItemCategory: async (category: Partial<ItemCategory>): Promise<ItemCategory> => {
    const response = await api.post('/inventory/categories/', category)
    return response.data
  },

  updateItemCategory: async (id: number, category: Partial<ItemCategory>): Promise<ItemCategory> => {
    const response = await api.patch(`/inventory/categories/${id}/`, category)
    return response.data
  },

  deleteItemCategory: async (id: number): Promise<void> => {
    await api.delete(`/inventory/categories/${id}/`)
  },

  // Inventory Items
  /** Deactivated items are included so they can be found and put back. */
  getInventoryItems: async (): Promise<InventoryItem[]> => {
    return getAll('/inventory/items/?include_inactive=1')
  },

  getInventoryItem: async (id: number): Promise<InventoryItem> => {
    const response = await api.get(`/inventory/items/${id}/`)
    return response.data
  },

  createInventoryItem: async (item: Partial<InventoryItem>): Promise<InventoryItem> => {
    const response = await api.post('/inventory/items/', item)
    return response.data
  },

  updateInventoryItem: async (id: number, item: Partial<InventoryItem>): Promise<InventoryItem> => {
    const response = await api.patch(`/inventory/items/${id}/`, item)
    return response.data
  },

  deleteInventoryItem: async (id: number): Promise<void> => {
    await api.delete(`/inventory/items/${id}/`)
  },

  // Suppliers
  getSuppliers: async (): Promise<Supplier[]> => {
    return getAll('/inventory/suppliers/?active_only=false')
  },

  getSupplier: async (id: number): Promise<Supplier> => {
    const response = await api.get(`/inventory/suppliers/${id}/`)
    return response.data
  },

  createSupplier: async (supplier: Partial<Supplier>): Promise<Supplier> => {
    const response = await api.post('/inventory/suppliers/', supplier)
    return response.data
  },

  updateSupplier: async (id: number, supplier: Partial<Supplier>): Promise<Supplier> => {
    const response = await api.patch(`/inventory/suppliers/${id}/`, supplier)
    return response.data
  },

  getAssetStatusHistory: async (id: number) => {
    const response = await api.get(`/inventory/assets/${id}/status-history/`)
    return response.data as AssetStatusChange[]
  },

  deleteSupplier: async (id: number): Promise<void> => {
    await api.delete(`/inventory/suppliers/${id}/`)
  },

  // Purchase Orders
  getPurchaseOrders: async (): Promise<PurchaseOrder[]> => {
    return getAll('/inventory/purchase-orders/')
  },

  getPurchaseOrder: async (id: number): Promise<PurchaseOrder> => {
    const response = await api.get(`/inventory/purchase-orders/${id}/`)
    return response.data
  },

  createPurchaseOrder: async (order: PurchaseOrderInput): Promise<PurchaseOrder> => {
    const response = await api.post('/inventory/purchase-orders/', order)
    return response.data
  },

  updatePurchaseOrder: async (id: number, order: Partial<PurchaseOrder>): Promise<PurchaseOrder> => {
    const response = await api.put(`/inventory/purchase-orders/${id}/`, order)
    return response.data
  },

  deletePurchaseOrder: async (id: number): Promise<void> => {
    await api.delete(`/inventory/purchase-orders/${id}/`)
  },

  submitPurchaseOrder: async (id: number): Promise<PurchaseOrder> => {
    const response = await api.post(`/inventory/purchase-orders/${id}/submit/`)
    return response.data
  },

  approvePurchaseOrder: async (id: number): Promise<PurchaseOrder> => {
    const response = await api.post(`/inventory/purchase-orders/${id}/approve/`)
    return response.data
  },

  markPurchaseOrderOrdered: async (id: number): Promise<PurchaseOrder> => {
    const response = await api.post(`/inventory/purchase-orders/${id}/mark-ordered/`)
    return response.data
  },

  receivePurchaseOrder: async (id: number, items: Array<{ id: number; quantity: number }>): Promise<PurchaseOrder> => {
    const response = await api.post(`/inventory/purchase-orders/${id}/receive/`, { items })
    return response.data
  },

  cancelPurchaseOrder: async (id: number, reason: string): Promise<PurchaseOrder> => {
    const response = await api.post(`/inventory/purchase-orders/${id}/cancel/`, { reason })
    return response.data
  },

  getStockMovements: async (): Promise<StockMovement[]> => {
    return getAll('/inventory/stock-movements/')
  },

  getItemMovements: async (item: number): Promise<StockMovement[]> => {
    return getAll(`/inventory/stock-movements/?item=${item}`)
  },

  getAlerts: async (): Promise<InventoryAlert[]> => {
    return getAll('/inventory/alerts/')
  },

  acknowledgeAlert: async (id: number, actionTaken: string): Promise<InventoryAlert> => {
    const response = await api.post(`/inventory/alerts/${id}/acknowledge/`, { action_taken: actionTaken })
    return response.data
  },

  getAssets: async (): Promise<Asset[]> => {
    return getAll('/inventory/assets/')
  },

  createAsset: async (asset: Partial<Asset>): Promise<Asset> => {
    const response = await api.post('/inventory/assets/', asset)
    return response.data
  },

  updateAsset: async (id: number, asset: Partial<Asset>): Promise<Asset> => {
    const response = await api.patch(`/inventory/assets/${id}/`, asset)
    return response.data
  },

  transitionAsset: async (id: number, status: Asset['status'], reason: string): Promise<Asset> => {
    const response = await api.post(`/inventory/assets/${id}/transition/`, { status, reason })
    return response.data
  },

  capitalizeAsset: async (id: number, paymentAccount: number): Promise<Asset> => {
    const response = await api.post(`/inventory/assets/${id}/capitalize/`, { payment_account: paymentAccount })
    return response.data
  },

  depreciateAsset: async (id: number, asOf: string): Promise<Asset> => {
    const response = await api.post(`/inventory/assets/${id}/depreciate/`, { as_of: asOf })
    return response.data
  },

  disposeAsset: async (
    id: number,
    data: { disposal_date: string; proceeds: string; receipt_account?: number; reason?: string },
  ): Promise<Asset> => {
    const response = await api.post(`/inventory/assets/${id}/dispose/`, data)
    return response.data
  },

  getMaintenanceRecords: async (): Promise<MaintenanceRecord[]> => {
    return getAll('/inventory/maintenance/')
  },

  createMaintenanceRecord: async (record: Partial<MaintenanceRecord>): Promise<MaintenanceRecord> => {
    const response = await api.post('/inventory/maintenance/', record)
    return response.data
  },

  transitionMaintenance: async (id: number, status: MaintenanceRecord['status'], details?: {
    work_performed?: string
    parts_replaced?: string
    recommendations?: string
  }): Promise<MaintenanceRecord> => {
    const response = await api.post(`/inventory/maintenance/${id}/transition/`, { status, ...details })
    return response.data
  },

  // Statistics
  adjustStock: async (id: number, quantity: number, reason: string): Promise<void> => {
    await api.post(`/inventory/items/${id}/adjust-stock/`, { quantity, reason })
  },

  getInventoryStats: async (): Promise<InventoryStats> => {
    const response = await api.get('/inventory/statistics/')
    return response.data
  },
}
